import React from "react";
import gs, { colors } from "gstyles";
import { IconName } from "gstyles/styleguide/icons/generated-icons";
import Link from "components/common/Link";
import { Point, Image } from "components/common";
import { useT } from "providers/I18nProvider";

interface ShopProps {
  style?: React.CSSProperties;
  className?: string;
  slug?: string;

  // Card config
  cardHeight?: number;
  cardWidth?: number;

  // Badge config
  badgeLabel?: string;
  badgePosition?: "tr" | "tl" | "br" | "bl";

  // Data
  allowBooking?: boolean;
  thumbnail?: string;
  displayedAddress?: string;
  name?: string;
  rating?: number;
  // location?: [number, number]; // [longitude, latitude]

  // Computed data
  // numberOfBranches?: string;
  distance?: number;

  // Button
  onClick?: (props?: unknown) => void;

  // Other
  // packages?: string;
  iconNamePrice?: IconName;
  haveSlotDiscount?: boolean;

  // Price data
  originalPrice?: string;
  discountPrice?: string;
  discountPercent?: string;
}

// const CARD_CONFIG = {
//   cardHeight: 206,
//   cardWidth: 290,
//   iconNamePrice: "sun-fog",
// };

const BADGE_POSITIONS = {
  tr: "top-0 right-0",
  tl: "top-0 left-0",
  br: "bottom-0 right-0",
  bl: "bottom-0 left-0",
};

const TYPE_BADGE = {
  booking: {
    labelKey: "home.cardBooking",
    badgeStyle: "bg-green-500",
  },
  review: {
    labelKey: "home.cardReview",
    badgeStyle: "bg-blue-500",
  },
};

export default function Shop({
  style = {},
  className = "",
  slug = "",

  // Badge
  badgeLabel = "",
  badgePosition = "tr",

  // Data
  allowBooking = false,
  thumbnail = "/banner.jpg",
  displayedAddress = "",
  name = "",
  rating,
  // location,

  // Computed data
  // numberOfBranches = "",
  distance = undefined,

  // Button
  // onClick = () => {},

  // Other
  // packages = "",
  iconNamePrice = "sun-fog",
  haveSlotDiscount = false,

  // Price
  originalPrice = "",
  discountPrice = "",
  discountPercent = "",
}: // ...props
ShopProps) {
  // const config = {
  //   ...CARD_CONFIG,
  //   ...(cardWidth && { cardWidth }),
  //   ...(cardHeight && { cardHeight }),
  // };
  // console.log({ props });
  const t = useT();
  const bType = allowBooking ? "booking" : "review";
  const bItem = TYPE_BADGE[bType as keyof typeof TYPE_BADGE];
  const bPosition = BADGE_POSITIONS[badgePosition];

  const finalBadgeLabel = badgeLabel || (bItem ? t(bItem.labelKey) : "") || discountPercent;
  const showBadge = !!(bType || badgeLabel || discountPercent);
  // Chỉ hiện điểm khi shop THẬT SỰ có điểm (>0); chưa có đánh giá thì ẩn, không hiện "0"
  const showRating = typeof rating === "number" && rating > 0;
  const showSlotDiscount = haveSlotDiscount;

  return (
    <Link href={`/${slug}`} className="block w-full max-w-[360px] h-full">
      <div
        className={`group w-full h-full flex flex-col rounded-xl overflow-hidden shadow-md bg-white cursor-pointer border border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 ${className}`}
        style={{ ...style }}
        // onClick={onClick}
        // {...props}
      >
        {/* Ảnh và các badge */}
        <div className="relative shrink-0">
          <Image
            src={thumbnail}
            alt={name}
            wrapClassName="w-full aspect-[4/3] object-cover bg-gray-100 transition-transform duration-300 group-hover:scale-[1.03]"
            sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
          />
          {/* <img
          src={thumbnail}
          alt={name}
          className="w-full h-[140px] object-cover"
        /> */}

          {/* Badge chính (giảm giá hoặc booking/review) */}
          {showBadge && (
            <div className={`absolute ${bPosition} flex gap-2`}>
              <span
                className={`${
                  bItem?.badgeStyle || "bg-red-500"
                } text-white text-xs font-semibold px-2 py-0.5 rounded-bl-xl rounded-tr-xl`}
              >
                {finalBadgeLabel}
              </span>
            </div>
          )}

          {/* Rating badge */}
          {showRating && (
            <Point value={rating} position="tl" width={38} height={36} />
          )}
        </div>

        {/* Nội dung */}
        <div className="p-4 flex flex-col flex-1">
          <div className="font-semibold text-base text-gray-900 mb-1 line-clamp-2 min-h-[3rem] leading-6">
            {name}
          </div>

          <div className="flex items-start gap-1 text-sm text-gray-500 mb-1">
            <div className="mr-1 pt-[2px]">
              {gs.icons({
                name: "home",
                width: 16,
                color: "#6b7280",
              })}
            </div>
            <span className="line-clamp-2 min-h-[2.5rem] leading-5">{displayedAddress}</span>
          </div>

          {distance && (
            <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
              {gs.icons({
                name: "explore",
                width: 16,
                color: "#9ca3af",
              })}
              <span>{distance} km</span>
            </div>
          )}

          {/* Giá */}
          {discountPrice && (
            <div className="flex items-end gap-2">
              <div className="flex items-end gap-2">
                {gs.icons({
                  className: "mb-[1px]",
                  name: iconNamePrice || "sun-fog",
                  width: 24,
                  color: colors.primary,
                })}
                <span
                  className="text-primary font-semibold text-lg flex -mb-[1px]"
                  style={{ lineHeight: "normal" }}
                >
                  {discountPrice}
                </span>
              </div>
              {originalPrice && (
                <span className="text-gray-400 line-through text-sm">
                  {originalPrice}
                </span>
              )}
            </div>
          )}

          {/* Slot discount */}
          {showSlotDiscount && (
            <div className="flex items-center gap-1 pt-1">
              {gs.icons({
                name: "sale",
                width: 16,
                color: colors.voucher,
              })}
              <span className="text-orange-500 text-xs font-semibold">
                {t("home.cardSlotDiscount")}
              </span>
            </div>
          )}

          {/* CTA */}
          <div className="mt-auto pt-3 flex items-center justify-end text-sm font-semibold text-primary">
            <span className="inline-flex items-center gap-1 transition-all duration-300 group-hover:gap-2">
              {t("home.viewDetail")}
              <span aria-hidden>→</span>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
